<template>
    <Container>
        <!-- Header Actions -->
        <div class="w-full flex justify-between items-center my-4">
            <h1 class="text-2xl font-bold text-white">Edit Audiobook</h1>
            <Link href="/backstage/audiobooks"
                class="text-white rounded-md py-2 px-4 bg-nomad-900 hover:bg-nomad-800 transition-colors">
                Cancel
            </Link>
        </div>

        <form @submit.prevent="submit" class="max-w-4xl">
            <!-- Section: Basic Information -->
            <div class="bg-nomad-900 rounded-xl p-6 mb-4">
                <h2 class="text-lg font-semibold text-nomad-200 mb-4">Basic Information</h2>

                <div class="grid gap-4">
                    <div>
                        <label class="block text-sm font-medium text-nomad-300 mb-2">
                            Title <span class="text-red-400">*</span>
                        </label>
                        <input type="text" v-model="form.title"
                            class="w-full rounded-md bg-nomad-800 border-nomad-700 text-white focus:border-nomad-accent focus:ring-nomad-accent"
                            placeholder="Enter audiobook title" required />
                        <span v-if="form.errors.title" class="text-red-400 text-sm mt-1">{{ form.errors.title }}</span>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-nomad-300 mb-2">Description</label>
                        <textarea v-model="form.description"
                            class="w-full rounded-md bg-nomad-800 border-nomad-700 text-white focus:border-nomad-accent focus:ring-nomad-accent"
                            placeholder="Enter audiobook description" rows="4"></textarea>
                        <span v-if="form.errors.description" class="text-red-400 text-sm mt-1">{{
                            form.errors.description }}</span>
                    </div>
                </div>
            </div>

            <!-- Section: Author & Narrator -->
            <div class="bg-nomad-900 rounded-xl p-6 mb-4">
                <h2 class="text-lg font-semibold text-nomad-200 mb-4">Author & Narrator</h2>

                <div class="grid md:grid-cols-2 gap-4">
                    <div>
                        <label class="block text-sm font-medium text-nomad-300 mb-2">
                            Author <span class="text-red-400">*</span>
                        </label>
                        <Selector type="artists" v-model="form.author" placeholder="Select author" />
                        <span v-if="form.errors['author.*.id']" class="text-red-400 text-sm mt-1">{{
                            form.errors['author.*.id'] }}</span>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-nomad-300 mb-2">
                            Narrator <span class="text-red-400">*</span>
                        </label>
                        <Selector type="artists" v-model="form.narrator" placeholder="Select narrator" />
                        <span v-if="form.errors['narrator.*.id']" class="text-red-400 text-sm mt-1">{{
                            form.errors['narrator.*.id'] }}</span>
                    </div>
                </div>
            </div>

            <!-- Section: Media & Settings -->
            <div class="bg-nomad-900 rounded-xl p-6 mb-4">
                <h2 class="text-lg font-semibold text-nomad-200 mb-4">Media & Settings</h2>

                <div class="grid gap-4">
                    <div>
                        <label class="block text-sm font-medium text-nomad-300 mb-2">Cover Image</label>
                        <ImageUploader v-model="form.image" :image="form.image" />
                        <span v-if="form.errors.image" class="text-red-400 text-sm mt-1">{{ form.errors.image }}</span>
                    </div>

                    <div class="grid md:grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-nomad-300 mb-2">
                                Language <span class="text-red-400">*</span>
                            </label>
                            <select v-model="form.language"
                                class="w-full rounded-md bg-nomad-800 border-nomad-700 text-white focus:border-nomad-accent focus:ring-nomad-accent">
                                <option value="so">Somali</option>
                                <option value="en">English</option>
                                <option value="ar">Arabic</option>
                            </select>
                        </div>

                        <div>
                            <label class="block text-sm font-medium text-nomad-300 mb-2">Publish Date</label>
                            <input type="datetime-local" v-model="form.published_at"
                                class="w-full rounded-md bg-nomad-800 border-nomad-700 text-white focus:border-nomad-accent focus:ring-nomad-accent" />
                        </div>
                    </div>

                    <div>
                        <label class="flex items-center cursor-pointer">
                            <input type="checkbox"
                                class="rounded bg-nomad-800 border-nomad-700 text-nomad-accent focus:ring-nomad-accent"
                                id="is_premium" v-model="form.is_premium" />
                            <span class="ml-2 text-nomad-300">
                                Premium content
                                <span class="text-xs text-nomad-500 ml-1">(Requires subscription to access)</span>
                            </span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- Section: Chapters -->
            <Chapters :audiobook-id="audiobook.id" />

            <!-- Form Actions -->
            <div class="flex gap-3">
                <button type="submit"
                    class="bg-nomad-accent hover:bg-nomad-accent/80 text-nomad-900 font-semibold rounded-md py-3 px-6 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="form.processing">
                    <span v-if="form.processing">Saving...</span>
                    <span v-else>Save Changes</span>
                </button>

                <Link href="/backstage/audiobooks"
                    class="bg-nomad-800 hover:bg-nomad-700 text-white rounded-md py-3 px-6 transition-all hover:shadow-md inline-block">
                    Cancel
                </Link>

                <button type="button" @click="deleteAudiobook"
                    class="ml-auto bg-red-600 hover:bg-red-700 text-white rounded-md py-3 px-6 transition-colors">
                    Delete Audiobook
                </button>
            </div>
        </form>
    </Container>
</template>

<script setup>
import Container from "@/Pages/Backstage/Container.vue";
import Selector from "@/Pages/Backstage/Components/Selector.vue";
import ImageUploader from "@/Pages/Backstage/Components/ImageUploader.vue";
import Chapters from "./Chapters.vue";
import { Link, useForm, usePage, router } from "@inertiajs/vue3";

const { audiobook } = usePage().props;

const form = useForm({
    title: audiobook.title,
    description: audiobook.description,
    author: audiobook.author ? [audiobook.author] : null,
    narrator: audiobook.narrator ? [audiobook.narrator] : null,
    image: audiobook.cover_image,
    language: audiobook.language,
    is_premium: audiobook.is_premium,
    published_at: audiobook.published_at ? formatDatetimeLocal(audiobook.published_at) : null,
});

function formatDatetimeLocal(datetime) {
    if (!datetime) return null;
    const date = new Date(datetime);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function submit() {
    form.put(`/backstage/audiobooks/${audiobook.id}`);
}

function deleteAudiobook() {
    if (confirm('Are you sure you want to delete this audiobook? This action cannot be undone.')) {
        router.delete(`/backstage/audiobooks/${audiobook.id}`, {
            onSuccess: () => {
                router.visit('/backstage/audiobooks');
            }
        });
    }
}
</script>